import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ButtonDestinationEditor } from "../inspector/LandingInspectorControls";
import LandingCopyTextField from "../inspector/LandingCopyTextField";
import { normalizeEditableColor, registryFieldError, toColorInput } from "../inspector/inspectorModel";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { buildInternalDestinationOptions, getValueAtDescriptorPath } from "../elementEditing";

function ctaColorFallback(field, form) {
  const path = String(field?.path || "");
  const secondary = path.includes("hero.secondaryCtaStyle.");
  const key = path.split(".").pop();
  const primaryColor = form?.theme?.primaryColor || "#E44E31";
  const secondaryColor = form?.theme?.secondaryColor || "#494A98";

  if (key === "textColor" || key === "hoverTextColor") return "#ffffff";
  if (key === "backgroundColor") return secondary ? "#ffffff" : primaryColor;
  if (key === "hoverBackgroundColor") return secondary ? "#ffffff" : secondaryColor;
  if (key === "borderColor") return "rgba(255,255,255,.7)";
  if (key === "shadowColor") return "#000000";
  return field?.fallbackValue || field?.overrideSeedValue || "#000000";
}

function ctaColorContractError(field, value) {
  const candidate = String(value ?? "").trim();
  if (!candidate) return "";
  const registryError = registryFieldError(field, candidate);
  if (registryError) return registryError;
  const valid =
    candidate.toLowerCase() === "transparent" ||
    /^#[0-9a-fA-F]{3,8}$/.test(candidate) ||
    /^rgba?\(\s*[0-9.]+%?\s*,\s*[0-9.]+%?\s*,\s*[0-9.]+%?(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/.test(candidate) ||
    /^hsla?\(\s*[0-9.]+(?:deg)?\s*,\s*[0-9.]+%\s*,\s*[0-9.]+%(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/.test(candidate);
  return valid ? "" : "Usá HEX, rgb/rgba, hsl/hsla o transparent.";
}

function CtaColorFieldControl({ field, form, disabled, onCommit }) {
  const rawValue = getValueAtDescriptorPath(form, field.path, "");
  const ownValue = String(rawValue ?? "").trim();
  const inherited = !ownValue;
  const fallback = ctaColorFallback(field, form);
  const effective = ownValue || fallback;
  const pickerValue = toColorInput(effective || fallback || "#000000");
  const advancedFormat = Boolean(ownValue) && !/^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(ownValue);
  const [advancedOpen, setAdvancedOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(ownValue);
  const [draftError, setDraftError] = React.useState("");

  React.useEffect(() => {
    setDraft(ownValue);
    setDraftError("");
  }, [field.path, ownValue]);

  const commitExactValue = () => {
    const candidate = normalizeEditableColor(draft);
    const error = ctaColorContractError(field, candidate);
    setDraftError(error);
    if (error || candidate === ownValue) return;
    onCommit?.(field.path, candidate);
  };

  return (
    <Stack
      spacing={0.9}
      sx={{ p: 1.1, border: "1px solid rgba(25,36,47,.1)", borderRadius: 2, minWidth: 0 }}
    >
      <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 900, minWidth: 0 }}>{field.label}</Typography>
        <Chip
          size="small"
          label={inherited ? "Heredado" : "Personalizado"}
          variant={inherited ? "outlined" : "filled"}
        />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
        <Box
          component="input"
          type="color"
          aria-label={`Elegir color para ${field.label}`}
          value={pickerValue}
          disabled={disabled}
          onChange={(event) => onCommit?.(field.path, event.target.value)}
          sx={{
            width: 48,
            height: 40,
            p: 0.2,
            border: "1px solid rgba(25,36,47,.14)",
            borderRadius: 1.5,
            bgcolor: "transparent",
            cursor: disabled ? "default" : "pointer",
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: "1 1 150px", minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.66)", fontWeight: 850 }}>
            {inherited
              ? "Usando el estilo por defecto de WebProfit"
              : advancedFormat
                ? "Valor personalizado en formato avanzado"
                : "Color personalizado del CTA"}
          </Typography>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.35, minWidth: 0 }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 18,
                height: 18,
                borderRadius: 0.8,
                border: "1px solid rgba(25,36,47,.16)",
                bgcolor: effective || "transparent",
                backgroundImage: String(effective).toLowerCase() === "transparent"
                  ? "linear-gradient(45deg, rgba(25,36,47,.12) 25%, transparent 25%), linear-gradient(-45deg, rgba(25,36,47,.12) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(25,36,47,.12) 75%), linear-gradient(-45deg, transparent 75%, rgba(25,36,47,.12) 75%)"
                  : "none",
                backgroundSize: "8px 8px",
                backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
              {inherited ? `Referencia ${pickerValue.toUpperCase()}` : `Vista ${pickerValue.toUpperCase()}`}
            </Typography>
          </Stack>
        </Box>
        {!inherited ? (
          <Button
            size="small"
            disabled={disabled}
            aria-label={`Heredar ${field.label}`}
            onClick={() => onCommit?.(field.path, "")}
            sx={{ textTransform: "none", fontWeight: 850, flexShrink: 0 }}
          >
            Heredar
          </Button>
        ) : null}
      </Stack>

      <Box>
        <Button
          size="small"
          disabled={disabled}
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((open) => !open)}
          sx={{ px: 0, minWidth: 0, textTransform: "none", fontWeight: 800, color: "text.secondary" }}
        >
          {advancedOpen ? "Ocultar valor exacto" : "Valor exacto (avanzado)"}
        </Button>
        {advancedOpen ? (
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 0.6 }}
            label="Valor CSS exacto"
            value={draft}
            disabled={disabled}
            error={Boolean(draftError)}
            helperText={draftError || "Admite HEX, rgb/rgba, hsl/hsla y transparent. Vacío vuelve a heredar."}
            inputProps={{ maxLength: 100 }}
            onChange={(event) => {
              setDraft(event.target.value);
              if (draftError) setDraftError("");
            }}
            onBlur={commitExactValue}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitExactValue();
              }
              if (event.key === "Escape") {
                setDraft(ownValue);
                setDraftError("");
              }
            }}
          />
        ) : null}
      </Box>
    </Stack>
  );
}

function FieldControl({ field, form, baselineForm, disabled, onCommit }) {
  if (!field) return null;
  const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return <FormControlLabel control={<Switch checked={value !== false} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.checked)} />} label={field.label} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return <TextField fullWidth size="small" type="number" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }} onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    return <CtaColorFieldControl field={field} form={form} disabled={disabled} onCommit={onCommit} />;
  }
  return <LandingCopyTextField fullWidth size="small" label={field.label} field={field} path={field.path} form={form} baselineForm={baselineForm} value={value ?? ""} disabled={disabled} multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA} minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined} commitMode="change" onCommit={(nextValue) => onCommit?.(field.path, nextValue)} />;
}

export default function ButtonElementEditor({ descriptor, form, baselineForm, disabled, onCommit }) {
  const labelField = descriptor?.labelField;
  if (!labelField) return null;
  const labelValue = getValueAtDescriptorPath(form, labelField.path, "");
  const options = buildInternalDestinationOptions(form);
  const showField = descriptor?.showField;
  const requiresField = descriptor?.requiresProductsField || (descriptor?.extraFields || []).find((field) => /RequiresProducts$/.test(field?.path || ""));
  const remainingExtra = (descriptor?.extraFields || []).filter((field) => field !== requiresField);
  const styleFields = descriptor?.styleFields || [];
  const showIconField = descriptor?.showIconField;
  const iconField = descriptor?.iconField;
  const urlField = descriptor?.urlField;
  const behaviorField = descriptor?.behaviorField;
  const targetField = descriptor?.targetField;
  const sharedField = [labelField, urlField, showField, requiresField, behaviorField, targetField, ...remainingExtra]
    .find((field) => field?.shared);

  return (
    <Stack spacing={1.35}>
      <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
        {(descriptor?.chips || []).map((chip) => <Chip key={chip} size="small" label={chip} variant="outlined" />)}
        {sharedField ? <Chip size="small" label="Se usa también en otras secciones" variant="outlined" /> : null}
        {requiresField && getValueAtDescriptorPath(form, requiresField.path, false) ? <Chip size="small" label="Catálogo" /> : null}
        {showIconField && getValueAtDescriptorPath(form, showIconField.path, false) ? <Chip size="small" label="Con icono" /> : null}
      </Stack>

      <Box sx={{ p: 1.2, borderRadius: 2.5, border: "1px solid rgba(25,36,47,.1)", bgcolor: "rgba(25,36,47,.025)" }}>
        <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.62)" }}>Vista previa</Typography>
        <Box sx={{ mt: 0.8, display: "inline-flex", px: 2, py: 1, borderRadius: 999, bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 900 }}>
          {labelValue || "Botón"}
        </Box>
      </Box>

      <LandingCopyTextField fullWidth size="small" label={labelField.label || "Texto del botón"} field={labelField} path={labelField.path} form={form} baselineForm={baselineForm} value={labelValue} disabled={disabled} commitMode="change" onCommit={(nextValue) => onCommit?.(labelField.path, nextValue)} />

      {showField ? <FieldControl field={showField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}
      {behaviorField ? <FieldControl field={behaviorField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}
      {targetField ? <FieldControl field={targetField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}

      {urlField ? (
        urlField.destinationPicker ? (
          <ButtonDestinationEditor field={urlField} value={getValueAtDescriptorPath(form, urlField.path, "")} options={options} disabled={disabled} onCommit={onCommit} />
        ) : <FieldControl field={urlField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
      ) : (!targetField ? <Chip size="small" label="Destino definido por esta sección" variant="outlined" sx={{ alignSelf: "flex-start" }} /> : null)}

      {requiresField ? <FieldControl field={requiresField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}
      {remainingExtra.map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}

      {showIconField || iconField ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Icono</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{showIconField ? <FieldControl field={showIconField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}{iconField && (!showIconField || getValueAtDescriptorPath(form, showIconField.path, true) !== false) ? <FieldControl field={iconField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}</Stack></AccordionDetails>
        </Accordion>
      ) : null}

      {(descriptor?.megaMenu || (descriptor?.megaMenuFields || []).length || (descriptor?.megaMenuOverrideFields || []).length) ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Configurar mega menú</Typography></AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {(descriptor?.megaMenuOverrideFields || []).map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}
              {(descriptor?.megaMenuFields || []).map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {styleFields.length ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia y hover</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{styleFields.map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
